// The generated header every WGSL world pass carries, and the shared body
// the twins will take.
//
// GLSL states its contract as text: a `#version` line, four precision
// pragmas, one `uniform` line per name and one `in` line per varying. None
// of that survives the port. WGSL has no preprocessor and no precision
// statement, every uniform is a member of ONE struct, and the varyings are a
// struct BOTH stages read. So the header is GENERATED from the same
// `UniformSpec[]` the WebGL2 half is configured from, and a twin writes
// bodies alone.
//
// `PRELUDE_BODY_WGSL` is the port of `../prelude.ts` `PRELUDE_BODY` — the
// hex topology, the plane accessors, the kernel and the coverage helpers.
// `glMod` is not here: no pass of this directory calls it, and it lives in
// `common.ts` with the other twins of a GLSL builtin.

import type { UniformSpec } from '../prelude';
import { wgslBindings, wgslUniformStruct } from './layout';

/**
 * The varyings every world and screen pass carries.
 *
 * GLSL declares them twice, once `out` in the vertex source and once `in` in
 * the fragment source. WGSL declares them ONCE: the vertex entry returns
 * this struct and the fragment entry takes it.
 */
const VARYINGS_WORLD = `struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) vWorld: vec2f,
  @location(1) vUV: vec2f,
}
`;

/**
 * The shared body of every world pass, ported from `../prelude.ts`.
 *
 * It is a function-for-function twin of `PRELUDE_BODY`: the same names, the
 * same order and the same arithmetic. `twins.test.ts` pins the names, and a
 * reader holds the two files side by side to read the rest.
 *
 * `cellAt` is still the line-for-line transcription of `world.ts cellRowCol`,
 * with `jsRound` in place of a builtin `round`, because GLSL and WGSL both
 * take a half to the nearest EVEN and JavaScript takes it up. The three
 * disagree on exactly the cell boundaries, which is where a border sits.
 *
 * The precision pragmas of the GLSL half have no twin. WGSL has no precision
 * statement: `f32` is a 32-bit float everywhere, so the highp question the
 * GLSL header answers cannot arise here.
 *
 * Three rules of the port show up in every function below:
 *
 *  * every uniform read is a member of the ONE generated block, so `uHexSize`
 *    is `U.uHexSize` and a bare name would not compile;
 *  * a plane read is `textureLoad`, which answers 0 outside the texture, and
 *    the `inGrid` guard stays an `if` because `select` evaluates both arms;
 *  * an `out` parameter is a `ptr<function, T>`, and the callee writes
 *    through it as `(*name)`.
 */
export const PRELUDE_BODY_WGSL = `
const SQ3: f32 = 1.7320508075688772;

// world.ts:116-119 HEX_OFFSETS — even rows then odd rows. Direction k faces
// the edge between corners k and k+1.
//
// The two parities are two arrays and not one of twelve, because the index
// then holds only the loop counter. Every walk over the six directions is a
// constant-bounded loop, so the compiler unrolls it and each read becomes a
// constant; one array of twelve would carry the row parity in the index and
// the whole table would leave the register file as an indexable temporary
// (R431, the GPU work).
//
// A module-scope const array is the WGSL form of the GLSL const table. A
// function with a switch is the fallback, if a driver refuses to index the
// array with a loop counter.
const HEX_EVEN = array<vec2i, 6>(
  vec2i( 1,  0), vec2i( 0,  1), vec2i(-1,  1), vec2i(-1,  0), vec2i(-1, -1), vec2i( 0, -1));
const HEX_ODD = array<vec2i, 6>(
  vec2i( 1,  0), vec2i( 1,  1), vec2i( 0,  1), vec2i(-1,  0), vec2i( 0, -1), vec2i( 1, -1));

fn jsRound(x: f32) -> f32 { return floor(x + 0.5); }

fn cellAt(p: vec2f) -> vec2i {               // world.ts cellRowCol, bit-compatible
  let s = U.uHexSize;
  let px = p.x - 0.5 * SQ3 * s;
  let py = p.y - s;
  let qf = (SQ3 / 3.0 * px - py / 3.0) / s;
  let rf = (2.0 / 3.0 * py) / s;
  let sf = -qf - rf;
  var q = jsRound(qf);
  var r = jsRound(rf);
  let z = jsRound(sf);
  let dq = abs(q - qf);
  let dr = abs(r - rf);
  let dz = abs(z - sf);
  if (dq > dr && dq > dz) { q = -r - z; }
  else if (dr > dz) { r = -q - z; }
  let row = i32(r);
  // The row may be negative, and the shift must stay ARITHMETIC to keep the
  // odd-r column of the reference. It is arithmetic in both languages: GLSL
  // and WGSL both shift a signed integer with its sign. WGSL asks for the
  // count as a u32, which is what the u suffix says.
  let col = i32(q) + ((row - (row & 1)) >> 1u);
  return vec2i(col, row);                    // may lie outside the grid
}

fn cellCenter(c: vec2i) -> vec2f {           // world.ts:128-131
  return vec2f(SQ3 * U.uHexSize * (f32(c.x) + 0.5 * f32(c.y & 1) + 0.5),
               1.5 * U.uHexSize * f32(c.y) + U.uHexSize);
}

fn neighbor(c: vec2i, k: i32) -> vec2i {
  if ((c.y & 1) == 1) { return c + HEX_ODD[k]; }
  return c + HEX_EVEN[k];
}

fn inGrid(c: vec2i) -> bool {
  return c.x >= 0 && c.y >= 0 && c.x < U.uGrid.x && c.y < U.uGrid.y;
}

fn hexCorner(k: i32) -> vec2f {              // world.ts:133-136
  let a = radians(60.0 * f32(k) - 30.0);
  return U.uHexSize * vec2f(cos(a), sin(a));
}

// A cell outside the grid is open and unowned. That is what makes the world
// edge a border the shader strokes.
fn ownerOf(c: vec2i) -> i32 {
  if (!inGrid(c)) { return 0; }
  return i32(textureLoad(uOwners, c, 0).r);
}

fn kindOf(c: vec2i) -> i32 {
  if (!inGrid(c)) { return 0; }
  return i32(textureLoad(uBlocked, c, 0).r);
}

// K(d) = (1 - d²/R²)² for d < R. C¹ at R, so the summed field has no seam
// where a cell drops out of the ring. dk is the analytic gradient.
//
// GLSL says out; WGSL says a function pointer. The caller passes the address
// of its own variable, and the callee writes through it.
fn kern(p: vec2f, c: vec2f, invR2: f32, k: ptr<function, f32>, dk: ptr<function, vec2f>) {
  let v = p - c;
  let u = max(0.0, 1.0 - dot(v, v) * invR2);
  (*k) = u * u;
  (*dk) = (-4.0 * u * invR2) * v;
}

// Box-filter coverage of a stroke of half-width hw at unsigned distance d.
// px is one device pixel measured in world px.
fn lineCov(d: f32, hw: f32, px: f32) -> f32 {
  return clamp((min(d + 0.5 * px, hw) - max(d - 0.5 * px, -hw)) / px, 0.0, 1.0);
}

// Coverage of the region a signed distance calls inside (sd > 0 is inside),
// box-filtered over one device pixel.
fn fillCov(sd: f32, px: f32) -> f32 { return clamp(sd / px + 0.5, 0.0, 1.0); }

// Premultiplied source-over.
fn over(dst: vec4f, c: vec3f, a: f32) -> vec4f { return vec4f(c * a, a) + dst * (1.0 - a); }

// One device pixel in world px.
fn worldPx() -> f32 { return 1.0 / max(U.uZoom * U.uDpr, 1e-6); }
`;

export interface WorldHeaderOpts {
  /**
   * The varying struct, where the pass does not read the shared one.
   *
   * One struct serves BOTH stages, so a pass that carries a varying of its
   * own — the outline stroke's signed distance, a batch's colour — states
   * the whole struct and cannot extend the shared one from the outside.
   */
  varyings?: string;
}

/**
 * The whole header of one world pass: the uniform struct, the bindings and
 * the varyings.
 *
 * The specs are the WHOLE block in order — the driver's, the shared
 * contract's and the pass's own — which is exactly what `ProgramSource`
 * carries, so a header can never name a uniform the packer does not write.
 */
export function worldHeaderWgsl(
  specs: readonly UniformSpec[], opts: WorldHeaderOpts = {},
): string {
  return [
    wgslUniformStruct(specs),
    wgslBindings(specs),
    opts.varyings ?? VARYINGS_WORLD,
  ].join('\n');
}
